/** Set only by the mobile entry (commands-mobile.ts). Keeping the NAA import
 *  out of this module keeps MSAL and webpack's chunk loader out of the
 *  desktop/web bundles, where the classic Outlook event runtime has no DOM. */
let naaProvider;
export function setNaaProvider(p) {
    naaProvider = p;
}
/** Office SSO errors after which we try nested app auth instead. 13000: the host
 *  or account doesn't support getAccessToken (what Outlook on Android/iOS
 *  reports; Microsoft also files a missing WebApplicationInfo block under it).
 *  13004 is documented as "Invalid Resource", usually a manifest/domain
 *  mismatch; we accept it defensively so a phone still reaches NAA if it reports
 *  that for a manifest with no SSO block. It is not documented host behaviour.
 *  Anything else (13003 wrong user type, a consent problem, ...) is a real
 *  failure and is rethrown. */
export function isUnsupportedSsoError(err) {
    if (typeof err !== "object" || err === null)
        return false;
    const code = String(err.code);
    return code === "13000" || code === "13004";
}
/** Legacy SSO first (unchanged on desktop/web); NAA only when legacy SSO
 *  reports it is unsupported or unconfigured AND NAA is available. Any other failure is
 *  rethrown so the caller's cached-signature fallback runs as before. */
export async function tokenWithFallback(deps) {
    try {
        return await deps.legacy();
    }
    catch (err) {
        if (isUnsupportedSsoError(err) && deps.naaSupported())
            return deps.naa();
        throw err;
    }
}
/**
 * Acquire an access token for our API for the current user.
 *
 * No sign-in / consent prompts in event-based runtime — if token fails,
 * caller must handle fallback (cached sig) gracefully.
 */
export function getOfficeToken() {
    return tokenWithFallback({
        legacy: () => OfficeRuntime.auth.getAccessToken({
            allowSignInPrompt: false,
            allowConsentPrompt: false,
            forMSGraphAccess: false,
        }),
        naa: () => {
            if (!naaProvider)
                throw new Error("NAA not available in this bundle");
            return naaProvider.naa();
        },
        naaSupported: () => naaProvider?.supported() ?? false,
    });
}
